import { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, DropdownItem, DropdownMenu, DropdownToggle, Input, UncontrolledDropdown } from 'reactstrap';
import type { ColumnDef } from '@tanstack/react-table';
import { Table } from '@g17eco/molecules/table';
import type { InitiativeGroup } from '../../../types/initiative-group';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import { useGetSubsidiaryGroupsQuery } from '@api/subsidiary-groups';
import { formatDateDisplay } from './utils';
import { Loader } from '@g17eco/atoms/loader';
import Dashboard, { DashboardSection } from '@g17eco/molecules/dashboard';
import { getErrorMessage } from '@utils/error';
import { DeleteSubsidiaryGroupModal } from './DeleteSubsidiaryGroupModal';
import { DuplicateSubsidiaryGroupModal } from './DuplicateSubsidiaryGroupModal';

interface SelectedGroup {
  groupId: string;
  groupName: string;
}

interface Props {
  initiativeId: string;
}

export const SubsidiaryGroupsTable = ({ initiativeId }: Props) => {
  const history = useHistory();
  const { data: subsidiaryGroups, isLoading, error } = useGetSubsidiaryGroupsQuery(initiativeId);
  const [search, setSearch] = useState('');

  const [deletingGroup, setDeletingGroup] = useState<SelectedGroup | null>(null);
  const [duplicatingGroup, setDuplicatingGroup] = useState<SelectedGroup | null>(null);

  const handleEdit = (groupId: string) => {
    history.push(generateUrl(ROUTES.EDIT_SUBSIDIARY_GROUP, { initiativeId, groupId }));
  };

  const handleCreateNew = () => {
    history.push(generateUrl(ROUTES.CREATE_SUBSIDIARY_GROUP, { initiativeId }));
  };

  const columns: ColumnDef<InitiativeGroup>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => row.original.name,
    },
    {
      id: 'subsidiaryCount',
      header: 'No of Subsidiaries',
      accessorFn: (row) => row.group.length,
    },
    {
      accessorKey: 'created',
      header: 'Created',
      cell: ({ row }) => formatDateDisplay(row.original.created),
    },
    {
      id: 'lastUpdated',
      header: 'Last updated',
      cell: ({ row }) => formatDateDisplay(row.original.updated),
    },
    {
      id: 'actions',
      header: '',
      cell: (c) => (
        <UncontrolledDropdown>
          <DropdownToggle outline color='transparent' className='px-2 btn btn-outline-transparent'>
            <i className='fas fa-bars'></i>
          </DropdownToggle>
          <DropdownMenu end>
            <DropdownItem onClick={() => handleEdit(c.row.original._id)}>
              <i className='fal fa-pencil mr-2' />
              Edit
            </DropdownItem>
            <DropdownItem
              onClick={() => setDuplicatingGroup({ groupId: c.row.original._id, groupName: c.row.original.name })}
            >
              <i className='fal fa-copy mr-2' />
              Duplicate
            </DropdownItem>
            <DropdownItem
              onClick={() => setDeletingGroup({ groupId: c.row.original._id, groupName: c.row.original.name })}
            >
              <i className='fal fa-trash mr-2 text-ThemeDangerMedium' />
              Delete
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  const filteredData = useMemo(() => {
    return (subsidiaryGroups ?? []).filter((g) => g.name.toLowerCase().includes(search.toLowerCase()));
  }, [subsidiaryGroups, search]);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Dashboard>
        <DashboardSection title='Unable to load subsidiary groups'>
          <>{getErrorMessage(error)}</>
        </DashboardSection>
      </Dashboard>
    );
  }

  return (
    <>
      <div className='d-flex gap-3 justify-content-between mb-3'>
        <Input
          placeholder='Search for subsidiary group'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className='flex-grow-1'
        />
        <Button
          color='primary'
          onClick={handleCreateNew}
          className='text-nowrap d-flex align-items-center gap-2'
        >
          <i className='fal fa-list-tree' />
          Create new group
        </Button>
      </div>

      {filteredData.length === 0 ? (
        <div className='text-center py-5 text-ThemeTextMedium'>
          {search ? 'No groups match your search' : 'No available groups'}
        </div>
      ) : (
        <Table data={filteredData} columns={columns} pageSize={10} responsive />
      )}

      <DeleteSubsidiaryGroupModal
        initiativeId={initiativeId}
        group={deletingGroup}
        onClose={() => setDeletingGroup(null)}
      />

      <DuplicateSubsidiaryGroupModal
        key={duplicatingGroup?.groupId}
        initiativeId={initiativeId}
        group={duplicatingGroup}
        onClose={() => setDuplicatingGroup(null)}
      />
    </>
  );
};
